import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSearch, FiArrowLeft,
  FiCheck, FiXCircle, FiEye, FiPrinter, FiPackage, FiRefreshCw
} from 'react-icons/fi';
import {
  collection, getDocs, doc, query, orderBy
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getVendorsCached, getItemsCached, getEmployeesCached, getJobsCached, getLocationsCached } from '../../utils/masterDataStore';
import { colors, spacing, borderRadius, typography, shadows } from '../../theme/colors';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea,
  AddLineButton, LineTableContainer, LineTable, LineTh, LineTd, LineInput, LineSelect, RemoveLineBtn,
  TotalsSection, TotalsTable, TotalRow, SortableTh, SortContext
} from '../../components/SharedStyles';
import SizeLineRow from '../../components/SizeLineRow';
import PageLayout from '../../components/PageLayout';
import VendorSelect from '../../components/VendorSelect';
import ItemSelect from '../../components/ItemSelect';
import AccountSelect from '../../components/AccountSelect';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import { formatAmount } from '../../utils/documentNumbering';
import {
  createTransaction, updateTransaction, deleteTransaction,
  updateTransactionStatus
} from '../../utils/transactionHelper';
import { computeLandedCost } from '../../utils/landedCostHelper';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import {
  isFutureDate, isLargeTransaction, LARGE_TRANSACTION_THRESHOLD,
  checkDuplicateVendorInvoice
} from '../../utils/validationHelpers';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import LineUnitPicker from '../../components/LineUnitPicker';
import { seedLineUnitFromItem, applyUnitChangeToLine } from '../../utils/unitConversion';
import { recomputeSizeLine } from '../../utils/sizeHelper';

const TRAN_TYPE_CODE = 'IPI';

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Posted: colors.info.main,
  Partial: '#9C27B0',
  Paid: colors.success.main,
  Cancelled: colors.error.main,
};

const SHIPMENT_MODES = ['Sea', 'Air', 'Land', 'Courier'];
const INCOTERMS = ['EXW', 'FOB', 'CFR', 'CIF', 'DDP', 'DAP'];
const COMMON_CURRENCIES = ['USD', 'EUR', 'GBP', 'CNY', 'AED', 'SAR', 'JPY', 'TRY'];

const EMPTY_LINE = {
  itemId: '', itemCode: '', itemName: '', description: '',
  unitOfMeasure: '',
  unitCode: '',
  quantity: 1,
  unitPriceForeign: 0,
  discountAmountForeign: 0,
  weight: 0,
  volume: 0,
  trackingType: 'None',
  batchNumber: '', expiryDate: '', serialNumbers: []
};

const EMPTY_CHARGE = {
  chargeName: '', type: 'percentage', value: 0, base: 'cif',
  accountId: '', accountCode: '', accountName: '',
  claimAccountId: '', claimAccountCode: '', claimAccountName: '',
  capitalize: true, allocationMethod: 'value'
};

const ImportPurchaseInvoice = () => {
  const location = useLocation();
  const { activeCompany, activeFinancialYear, currentUser, companyUser, activeBranch } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('transactions', 'importPurchaseInvoice');
  const showBranchCol = activeBranch && activeBranch.id === 'all';
  // Base currency — labels follow company settings (PKR / AED / USD / etc.)
  const baseCurrency = activeCompany?.currencyCode || 'PKR';

  // Print config (per-company document format overrides)
  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => {
    if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig);
  }, [activeCompany]);

  const handlePrint = (inv) => {
    printDocument('importPurchaseInvoice', inv, activeCompany, printConfig, activeBranch);
  };

  // ─── List state ───
  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  // No footer SummaryBar on this page — summarySpec stays null.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: invoices,
    loading,
    refresh: loadInvoices,
    ensureDoc,
    applyLocal,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: null
  });

  // ─── Form state ───
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    docDate: new Date().toISOString().split('T')[0],
    partyId: '', partyName: '',
    vendorInvoiceNo: '', vendorInvoiceDate: '',
    foreignCurrency: 'USD',
    exchangeRate: 0,
    gdNumber: '', gdDate: '',
    blAwbNumber: '',
    containerNumber: '',
    shipmentMode: 'Sea',
    countryOfOrigin: '',
    portOfLoading: '',
    portOfDischarge: '',
    incoterms: 'CIF',
    estimatedArrival: '', actualArrival: '',
    notes: '', internalNotes: '',
    status: 'Draft',
    employeeId: '', employeeName: '',
    jobId: '', jobName: '',
    locationId: '', locationName: ''
  });

  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);
  const [charges, setCharges] = useState([]);
  const [appliedTemplateId, setAppliedTemplateId] = useState('');

  // ─── Lookups ───
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [chargeTemplates, setChargeTemplates] = useState([]);

  // ─── Load data ───
  // Invoice list now comes from useDocListPagination above (loadInvoices = its
  // refresh). The old full-history getTransactions load lived here.

  const loadLookups = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Tier 1 (2026-07-27, Firebase cost): vendors + items come from the
      // device-local master cache (0 reads/open); the rest keep their getDocs.
      const [vRows, iRows, eRows, jRows, lRows, tSnap] = await Promise.all([
        getVendorsCached(activeCompany.id),
        getItemsCached(activeCompany.id),
        getEmployeesCached(activeCompany.id),
        getJobsCached(activeCompany.id),
        getLocationsCached(activeCompany.id),
        getDocs(query(collection(db, 'companies', activeCompany.id, 'importChargeTemplates'), orderBy('templateName')))
      ]);
      setVendors([...vRows].sort((a, b) => (a.vendorName || '').localeCompare(b.vendorName || '')));
      setItems(iRows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
      setEmployees([...eRows]);
      setJobs([...jRows]);
      setLocations([...lRows]);
      setChargeTemplates(tSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.isActive !== false));
    } catch (e) { console.error('Load lookups error:', e); }
  }, [activeCompany]);

  // NOTE: the invoice list itself loads inside useDocListPagination — not here.
  useEffect(() => { loadLookups(); }, [loadLookups]);

  // ─── Live landed cost computation ───
  const landed = useMemo(() => {
    return computeLandedCost({
      lines: lineItems.map(l => ({
        ...l,
        quantity: Number(l.quantity) || 0,
        unitPriceForeign: Number(l.unitPriceForeign) || 0,
        discountAmountForeign: Number(l.discountAmountForeign) || 0,
        weight: Number(l.weight) || 0,
        volume: Number(l.volume) || 0
      })),
      charges,
      exchangeRate: Number(formData.exchangeRate) || 0
    });
  }, [lineItems, charges, formData.exchangeRate]);

  // ─── Form open/reset ───
  const resetForm = () => {
    setEditingInvoice(null);
    setAppliedTemplateId('');
    setFormData({
      docDate: new Date().toISOString().split('T')[0],
      partyId: '', partyName: '',
      vendorInvoiceNo: '', vendorInvoiceDate: '',
      foreignCurrency: 'USD',
      exchangeRate: 0,
      gdNumber: '', gdDate: '',
      blAwbNumber: '',
      containerNumber: '',
      shipmentMode: 'Sea',
      countryOfOrigin: '',
      portOfLoading: '',
      portOfDischarge: '',
      incoterms: 'CIF',
      estimatedArrival: '', actualArrival: '',
      notes: '', internalNotes: '',
      status: 'Draft',
      employeeId: '', employeeName: '',
      jobId: '', jobName: '',
      locationId: '', locationName: ''
    });
    setLineItems([{ ...EMPTY_LINE }]);
    setCharges([]);
  };

  const openNew = () => {
    if (!activeFinancialYear?.id) {
      alert('Please activate a financial year first.');
      return;
    }
    if (activeBranch && activeBranch.id === 'all') {
      alert('Please pick a specific branch before creating an Import Purchase Invoice.');
      return;
    }
    resetForm();
    setViewMode('form');
  };

  const openEdit = (inv) => {
    setEditingInvoice(inv);
    setFormData({
      docDate: inv.docDate || new Date().toISOString().split('T')[0],
      partyId: inv.partyId || '', partyName: inv.partyName || '',
      vendorInvoiceNo: inv.vendorInvoiceNo || '', vendorInvoiceDate: inv.vendorInvoiceDate || '',
      foreignCurrency: inv.foreignCurrency || 'USD',
      exchangeRate: inv.exchangeRate || 0,
      gdNumber: inv.gdNumber || '', gdDate: inv.gdDate || '',
      blAwbNumber: inv.blAwbNumber || '',
      containerNumber: inv.containerNumber || '',
      shipmentMode: inv.shipmentMode || 'Sea',
      countryOfOrigin: inv.countryOfOrigin || '',
      portOfLoading: inv.portOfLoading || '',
      portOfDischarge: inv.portOfDischarge || '',
      incoterms: inv.incoterms || 'CIF',
      estimatedArrival: inv.estimatedArrival || '', actualArrival: inv.actualArrival || '',
      notes: inv.notes || '', internalNotes: inv.internalNotes || '',
      status: inv.status || 'Draft',
      employeeId: inv.employeeId || '', employeeName: inv.employeeName || '',
      jobId: inv.jobId || '', jobName: inv.jobName || '',
      locationId: inv.locationId || '', locationName: inv.locationName || ''
    });
    setLineItems((inv.lines || []).length > 0 ? inv.lines.map(l => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }]);
    setCharges((inv.importCharges || []).map(c => ({ ...EMPTY_CHARGE, ...c })));
    setAppliedTemplateId('');
    setViewMode('form');
  };

  const goBack = () => { setViewMode('list'); resetForm(); };

  // Handle navigation from reports (e.g., Journal Report clicking on document #).
  // With pagination the target may not be in the loaded page — ensureDoc
  // fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(transaction => {
      if (transaction) {
        openEdit(transaction);
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, ensureDoc]);

  // ─── Vendor / Item / Lookup pickers ───
  // VendorSelect / ItemSelect call onChange(id) — we look up the full row from state.
  const handleVendorChange = (vendorId) => {
    const v = vendors.find(x => x.id === vendorId);
    setFormData(prev => ({
      ...prev,
      partyId: vendorId || '',
      partyName: v?.vendorName || ''
    }));
  };

  const handleItemPicked = (index, itemId) => {
    const item = items.find(x => x.id === itemId);
    const seeded = item
      ? seedLineUnitFromItem(item, { priceField: 'purchasePrice', defaultUnitField: 'defaultPurchaseUnit' })
      : { unitCode: '', unitOfMeasure: 'Pcs' };
    setLineItems(prev => prev.map((l, i) => {
      if (i !== index) return l;
      let next = {
        ...l,
        itemId: itemId || '',
        itemCode: item?.itemCode || '',
        itemName: item?.itemName || '',
        description: item?.description || '',
        unitOfMeasure: seeded.unitOfMeasure || item?.unitOfMeasure || 'Pcs',
        unitCode: seeded.unitCode || '',
        trackingType: item?.trackingType || 'None'
      };
      // Size mode: prime with item defaults and force unit to SQft. The IPI
      // landed-cost engine reads line.quantity for allocation, so feeding it
      // Total SQft slots in cleanly without any changes to landedCostHelper.
      if (item?.hasSize) {
        next.hasSize = true;
        next.sizeUnit = item.sizeUnit === 'cm' ? 'cm' : 'inches';
        next.length = Number(item.defaultLength) || 0;
        next.height = Number(item.defaultHeight) || 0;
        next.thickness = Number(item.defaultThickness) || 0;
        next.pieces = next.pieces || 1;
        next.unitOfMeasure = 'SQft';
        next.unitCode = 'SQft';
        next = recomputeSizeLine(next);
      } else {
        next.hasSize = false;
      }
      return next;
    }));
  };

  const handleUnitChange = (index, unitCode) => {
    const line = lineItems[index];
    if (!line || !line.itemId) return;
    const item = items.find(x => x.id === line.itemId);
    if (!item) return;
    const patch = applyUnitChangeToLine(item, unitCode, { priceField: 'purchasePrice' });
    setLineItems(prev => prev.map((l, i) => i === index ? { ...l, unitCode: patch.unitCode, unitOfMeasure: patch.unitOfMeasure } : l));
  };

  // ─── Line handlers ───
  const updateLine = (index, patch) => {
    setLineItems(prev => prev.map((l, i) => {
      if (i !== index) return l;
      let merged = { ...l, ...patch };
      // Size-mode line edits: recompute Total SQft when any dimension changes.
      const sizeFields = ['length', 'height', 'thickness', 'pieces', 'sizeUnit'];
      if (merged.hasSize && sizeFields.some(f => Object.prototype.hasOwnProperty.call(patch, f))) {
        merged = recomputeSizeLine(merged);
      }
      return merged;
    }));
  };
  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || (Number(line.quantity) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => setLineItems(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);

  // ─── Charge handlers ───
  const updateCharge = (index, patch) => {
    setCharges(prev => prev.map((c, i) => i === index ? { ...c, ...patch } : c));
  };
  const addCharge = () => setCharges(prev => [...prev, { ...EMPTY_CHARGE }]);
  const removeCharge = (index) => setCharges(prev => prev.filter((_, i) => i !== index));

  const applyTemplate = (templateId) => {
    setAppliedTemplateId(templateId);
    if (!templateId) return;
    const tpl = chargeTemplates.find(t => t.id === templateId);
    if (!tpl) return;
    setCharges((tpl.charges || []).map(c => ({ ...EMPTY_CHARGE, ...c })));
  };

  // ─── Validation + Save ───
  const validate = () => {
    if (!formData.partyId) return 'Vendor is required.';
    if (!formData.foreignCurrency) return 'Foreign currency is required.';
    if (Number(formData.exchangeRate) <= 0) return 'Exchange rate must be greater than zero.';
    if (lineItems.length === 0) return 'Add at least one line item.';
    for (let i = 0; i < lineItems.length; i++) {
      const l = lineItems[i];
      if (!l.itemId) return `Line ${i + 1}: select an item.`;
      if (Number(l.quantity) <= 0) return `Line ${i + 1}: quantity must be > 0.`;
      if (Number(l.unitPriceForeign) < 0) return `Line ${i + 1}: unit price cannot be negative.`;
    }
    for (let i = 0; i < charges.length; i++) {
      const c = charges[i];
      if (!c.chargeName?.trim()) return `Charge ${i + 1}: name is required.`;
      if (!c.accountId) return `Charge ${i + 1}: select a Payable Account.`;
      if (!c.capitalize && !c.claimAccountId) return `Charge ${i + 1}: non-capitalized charges need a Claim Account.`;
    }
    if (!landed.isValid) return landed.errors.join(' | ');
    return null;
  };

  const buildTransactionData = (status) => {
    // Stamp landedUnitCost on each line (so stockFlowHelper picks it up)
    const enrichedLines = landed.lines.map((l, i) => ({
      itemId: l.itemId,
      itemCode: l.itemCode,
      itemName: l.itemName,
      description: l.description,
      unitOfMeasure: l.unitOfMeasure || 'Pcs',
      quantity: Number(l.quantity) || 0,
      // Foreign + base for storage / reports
      unitPriceForeign: Number(l.unitPriceForeign) || 0,
      discountAmountForeign: Number(l.discountAmountForeign) || 0,
      weight: Number(l.weight) || 0,
      volume: Number(l.volume) || 0,
      foreignTotal: l.foreignTotal,
      baseTotal: l.baseTotal,
      // Per-line allocation breakdown (read-only on edit)
      allocatedCharges: l.allocatedCharges,
      capitalizedAllocated: l.capitalizedTotal,
      nonCapitalizedAllocated: l.nonCapitalizedTotal,
      landedTotal: l.landedTotal,
      // CRITICAL: stockFlowHelper reads this for IPI to set unitCost
      landedUnitCost: l.landedUnitCost,
      // For consistency with PI line shape (used by some reports)
      unitPrice: l.landedUnitCost,
      unitCost: l.landedUnitCost,
      lineTotal: l.landedTotal,
      // Tracking
      trackingType: l.trackingType || 'None',
      batchNumber: l.batchNumber || '',
      expiryDate: l.expiryDate || '',
      serialNumbers: l.serialNumbers || []
    }));

    return {
      tranTypeCode: TRAN_TYPE_CODE,
      docDate: formData.docDate,
      financialYearId: activeFinancialYear?.id,
      branchId: activeBranch?.id || null,
      branchName: activeBranch?.branchName || null,
      partyType: 'vendor',
      partyId: formData.partyId,
      partyName: formData.partyName,
      // Header import-specific fields (preserved on the transaction doc)
      vendorInvoiceNo: formData.vendorInvoiceNo,
      vendorInvoiceDate: formData.vendorInvoiceDate,
      foreignCurrency: formData.foreignCurrency,
      exchangeRate: Number(formData.exchangeRate) || 0,
      gdNumber: formData.gdNumber,
      gdDate: formData.gdDate,
      blAwbNumber: formData.blAwbNumber,
      containerNumber: formData.containerNumber,
      shipmentMode: formData.shipmentMode,
      countryOfOrigin: formData.countryOfOrigin,
      portOfLoading: formData.portOfLoading,
      portOfDischarge: formData.portOfDischarge,
      incoterms: formData.incoterms,
      estimatedArrival: formData.estimatedArrival,
      actualArrival: formData.actualArrival,
      // Charges (raw + computed) — stored for edit + reports
      importCharges: charges,
      landedCharges: landed.charges,            // forwarded to subTransactionHelper
      foreignSubtotal: landed.summary.foreignSubtotal,
      baseSubtotal: landed.summary.baseSubtotal,
      capitalizedTotal: landed.summary.capitalizedTotal,
      nonCapitalizedTotal: landed.summary.nonCapitalizedTotal,
      inventoryValue: landed.summary.inventoryValue,
      // Standard transaction-level totals
      // subtotal must equal what hits the foreign vendor (in base currency) so reports treat it like PI
      subtotal: landed.summary.baseSubtotal,
      discountAmount: 0,
      taxableAmount: landed.summary.baseSubtotal,
      taxAmount: 0,
      whtRate: 0,
      whtAmount: 0,
      totalAmount: landed.summary.grandTotalOwed,
      paidAmount: 0,
      balanceAmount: landed.summary.grandTotalOwed,
      // Optional
      employeeId: formData.employeeId, employeeName: formData.employeeName,
      jobId: formData.jobId, jobName: formData.jobName,
      locationId: formData.locationId, locationName: formData.locationName,
      notes: formData.notes, internalNotes: formData.internalNotes,
      status,
      lines: enrichedLines
    };
  };

  const runPreSaveWarnings = async (status) => {
    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return false;
    }
    if (isLargeTransaction(landed.summary.grandTotalOwed)) {
      if (!window.confirm(`Grand total Rs ${formatAmount(landed.summary.grandTotalOwed)} exceeds Rs ${formatAmount(LARGE_TRANSACTION_THRESHOLD)}. Continue?`)) return false;
    }
    if (status === 'Posted' && formData.vendorInvoiceNo) {
      const dup = await checkDuplicateVendorInvoice(activeCompany.id, formData.partyId, formData.vendorInvoiceNo, editingInvoice?.id);
      if (dup) {
        if (!window.confirm(`Vendor invoice number "${formData.vendorInvoiceNo}" already exists for this vendor. Continue?`)) return false;
      }
    }
    return true;
  };

  const handleSave = async (status = 'Draft') => {
    if (!canEdit) { alert('You do not have permission to save.'); return; }
    // Add-only role (2026-11-04): this screen also opens saved invoices to view,
    // so the block is on saving a change, not on opening.
    if (editingInvoice && !canChange) { alert('You can add new records but cannot change saved ones.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    const err = validate();
    if (err) { alert(err); return; }
    if (status === 'Posted' && landed.summary.grandTotalOwed <= 0) {
      alert('Cannot post a zero-amount Import Invoice.');
      return;
    }
    const ok = await runPreSaveWarnings(status);
    if (!ok) return;

    setSaving(true);
    try {
      const data = buildTransactionData(status);
      let newTranData = null;
      if (editingInvoice) {
        await updateTransaction(activeCompany.id, editingInvoice.id, data, currentUser.uid);
      } else {
        newTranData = await createTransaction(activeCompany.id, data, currentUser.uid);
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedId = editingInvoice ? editingInvoice.id : newTranData?.id;
      const savedRecord = editingInvoice
        ? { ...editingInvoice, ...data, id: savedId, status }
        : { ...newTranData, status };
      applyLocal(prev => editingInvoice
        ? prev.map(r => r.id === savedId ? savedRecord : r)
        : [savedRecord, ...prev]);

      goBack();

      // Background refresh — reconciles with server data without blocking UI.
      loadInvoices().catch(err =>
        console.error('Background refresh failed:', err)
      );
    } catch (e) {
      console.error('Save IPI error:', e);
      alert('Failed to save: ' + (e?.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (inv, newStatus) => {
    if (!canEdit) { alert('You do not have permission.'); return; }
    if (newStatus === 'Posted' && missingLocation(activeCompany, inv.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!window.confirm(`Change status of ${inv.docNumber} to "${newStatus}"?`)) return;
    try {
      await updateTransactionStatus(activeCompany.id, inv.id, newStatus, currentUser.uid);
      await loadInvoices();
    } catch (e) { alert('Failed: ' + (e?.message || '')); }
  };

  const handleDelete = async (inv) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete ${inv.docNumber}? This removes the invoice + accounting + stock entries permanently.`)) return;
    try {
      await deleteTransaction(activeCompany.id, inv.id);
      await loadInvoices();
    } catch (e) { alert('Failed to delete: ' + (e?.message || '')); }
  };

  useDocumentShortcuts({
    onNew: openNew,
    onSave: () => handleSave(formData.status === 'Posted' ? 'Posted' : 'Draft'),
    viewMode,
    saving
  });

  // ─── Filtered list ───
  const filteredInvoices = useMemo(() => sort.sortItems(invoices.filter(inv => {
    const matchSearch = !searchQuery ||
      inv.docNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.partyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.vendorInvoiceNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.gdNumber?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = statusFilter === 'All' || inv.status === statusFilter;
    return matchSearch && matchStatus;
  })), [invoices, searchQuery, statusFilter, sort]);

  // ═══════════════════════════════════════════════════════════
  // RENDER — LIST VIEW
  // ═══════════════════════════════════════════════════════════
  if (viewMode === 'list') {
    return (
      <PageLayout pageTitle="Import Purchase Invoice" breadcrumb="Purchases / Import Purchase Invoice">
        <ActionBar>
          <SearchBox>
            <FiSearch />
            <input
              type="text"
              placeholder="Search by doc#, vendor, GD#, or vendor invoice#..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchBox>
          <FilterGroup>
            <StatusFilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="All">All Status</option>
              {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
            </StatusFilterSelect>
          </FilterGroup>
          {canEdit && (
            <TranAddButton onClick={openNew}>
              <FiPlus /> New Import Invoice
            </TranAddButton>
          )}
        </ActionBar>

        {loading ? (
          <LoadingMessage>Loading import invoices...</LoadingMessage>
        ) : filteredInvoices.length === 0 ? (
          <TranEmptyState>
            {searchQuery || statusFilter !== 'All'
              ? 'No import invoices match your filters.'
              : 'No Import Purchase Invoices yet. Click "New Import Invoice" to create one.'}
          </TranEmptyState>
        ) : (
          <TableContainer>
            <SortContext.Provider value={sort.ctxValue}>
            <Table>
              <thead>
                <tr>
                  <SortableTh field="docNumber">Doc #</SortableTh>
                  <SortableTh field="docDate">Date</SortableTh>
                  <SortableTh field="partyName">Vendor</SortableTh>
                  <SortableTh field="gdNumber">GD #</SortableTh>
                  <SortableTh field="foreignCurrency">Currency</SortableTh>
                  <SortableTh field="foreignSubtotal" style={{ textAlign: 'right' }}>Foreign</SortableTh>
                  <SortableTh field="inventoryValue" style={{ textAlign: 'right' }}>Inventory Value</SortableTh>
                  <SortableTh field="totalAmount" style={{ textAlign: 'right' }}>Total Owed</SortableTh>
                  {showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}
                  <SortableTh field="status">Status</SortableTh>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map(inv => (
                  <Tr key={inv.id}>
                    <Td><strong>{inv.docNumber}</strong></Td>
                    <Td>{inv.docDate}</Td>
                    <Td>{inv.partyName || '-'}</Td>
                    <Td>{inv.gdNumber || '-'}</Td>
                    <Td>
                      <CurrencyTag>{inv.foreignCurrency || 'USD'}</CurrencyTag>
                      <SmallRate>@ {inv.exchangeRate || 0}</SmallRate>
                    </Td>
                    <Td style={{ textAlign: 'right' }}>{(inv.foreignSubtotal || 0).toLocaleString()}</Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(inv.inventoryValue || 0)}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(inv.totalAmount || 0)}</Td>
                    {showBranchCol && <Td>{inv.branchName || '-'}</Td>}
                    <Td>
                      <StatusBadge style={{ background: STATUS_COLORS[inv.status] + '20', color: STATUS_COLORS[inv.status] }}>
                        {inv.status}
                      </StatusBadge>
                    </Td>
                    <Td>
                      <ActionGroup>
                        <IconBtn title="View / Edit" onClick={() => openEdit(inv)}><FiEye /></IconBtn>
                        {canChange && inv.status === 'Draft' && (
                          <IconBtn title="Edit" onClick={() => openEdit(inv)}><FiEdit2 /></IconBtn>
                        )}
                        {canEdit && inv.status === 'Draft' && (
                          <IconBtn title="Post" onClick={() => handleStatusChange(inv, 'Posted')}><FiCheck /></IconBtn>
                        )}
                        {canEdit && inv.status === 'Posted' && (
                          <IconBtn title="Cancel" onClick={() => handleStatusChange(inv, 'Cancelled')}><FiXCircle /></IconBtn>
                        )}
                        <IconBtn title="Print" onClick={() => handlePrint(inv)}><FiPrinter /></IconBtn>
                        {canDelete && (
                          <IconBtn $danger title="Delete" onClick={() => handleDelete(inv)}><FiTrash2 /></IconBtn>
                        )}
                      </ActionGroup>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            </SortContext.Provider>
          </TableContainer>
        )}

        <DocListPagination {...pagination} />
      </PageLayout>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // RENDER — FORM VIEW
  // ═══════════════════════════════════════════════════════════
  return (
    <PageLayout pageTitle="Import Purchase Invoice" breadcrumb="Purchases / Import Purchase Invoice">
      <FormHeader>
        <BackButton onClick={goBack}><FiArrowLeft /> Back</BackButton>
        <FormTitle>
          {editingInvoice ? `Edit ${editingInvoice.docNumber}` : 'New Import Purchase Invoice'}
        </FormTitle>
        <FormActions>
          {editingInvoice && (
            <SaveButton
              type="button"
              onClick={() => handlePrint(editingInvoice)}
              style={{ background: colors.text.secondary }}
            >
              <FiPrinter /> Print
            </SaveButton>
          )}
          {!(editingInvoice && editingInvoice.status && editingInvoice.status !== 'Draft' && editingInvoice.status !== 'Cancelled') && (
          <SaveButton type="button" onClick={() => handleSave('Draft')} disabled={saving}>
            Save Draft
          </SaveButton>
          )}
          <SaveButton
            type="button"
            onClick={() => handleSave('Posted')}
            disabled={saving}
            style={{ background: colors.success.main }}
          >
            {saving ? 'Saving...' : 'Save & Post'}
          </SaveButton>
        </FormActions>
      </FormHeader>

      {/* ───────── Header ───────── */}
      <FormCard>
        <SectionTitle>Document & Shipment</SectionTitle>
        <FormGrid>
          <FormGroup>
            <Label>Doc Date *</Label>
            <Input type="date" value={formData.docDate}
              onChange={(e) => setFormData({ ...formData, docDate: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label>Vendor (Foreign) *</Label>
            <VendorSelect
              vendors={vendors}
              selectedVendorId={formData.partyId}
              onChange={handleVendorChange}
              balanceOnDemand
            />
          </FormGroup>
          <FormGroup>
            <Label>Vendor Invoice No</Label>
            <Input value={formData.vendorInvoiceNo}
              onChange={(e) => setFormData({ ...formData, vendorInvoiceNo: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label>Vendor Invoice Date</Label>
            <Input type="date" value={formData.vendorInvoiceDate}
              onChange={(e) => setFormData({ ...formData, vendorInvoiceDate: e.target.value })} />
          </FormGroup>

          <FormGroup>
            <Label>Foreign Currency *</Label>
            <Select value={formData.foreignCurrency}
              onChange={(e) => setFormData({ ...formData, foreignCurrency: e.target.value })}>
              {COMMON_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Exchange Rate (1 {formData.foreignCurrency} = ? {baseCurrency}) *</Label>
            <Input type="number" step="0.0001" min="0"
              value={formData.exchangeRate}
              onChange={(e) => setFormData({ ...formData, exchangeRate: e.target.value })}
              placeholder="e.g. 280" />
          </FormGroup>

          <FormGroup>
            <Label>GD Number</Label>
            <Input value={formData.gdNumber}
              onChange={(e) => setFormData({ ...formData, gdNumber: e.target.value })}
              placeholder="Goods Declaration #" />
          </FormGroup>
          <FormGroup>
            <Label>GD Date</Label>
            <Input type="date" value={formData.gdDate}
              onChange={(e) => setFormData({ ...formData, gdDate: e.target.value })} />
          </FormGroup>

          <FormGroup>
            <Label>BL / AWB No</Label>
            <Input value={formData.blAwbNumber}
              onChange={(e) => setFormData({ ...formData, blAwbNumber: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label>Container No</Label>
            <Input value={formData.containerNumber}
              onChange={(e) => setFormData({ ...formData, containerNumber: e.target.value })} />
          </FormGroup>

          <FormGroup>
            <Label>Shipment Mode</Label>
            <Select value={formData.shipmentMode}
              onChange={(e) => setFormData({ ...formData, shipmentMode: e.target.value })}>
              {SHIPMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Incoterms</Label>
            <Select value={formData.incoterms}
              onChange={(e) => setFormData({ ...formData, incoterms: e.target.value })}>
              {INCOTERMS.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Country of Origin</Label>
            <Input value={formData.countryOfOrigin}
              onChange={(e) => setFormData({ ...formData, countryOfOrigin: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label>Port of Loading</Label>
            <Input value={formData.portOfLoading}
              onChange={(e) => setFormData({ ...formData, portOfLoading: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label>Port of Discharge</Label>
            <Input value={formData.portOfDischarge}
              onChange={(e) => setFormData({ ...formData, portOfDischarge: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label>Estimated Arrival</Label>
            <Input type="date" value={formData.estimatedArrival}
              onChange={(e) => setFormData({ ...formData, estimatedArrival: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label>Actual Arrival</Label>
            <Input type="date" value={formData.actualArrival}
              onChange={(e) => setFormData({ ...formData, actualArrival: e.target.value })} />
          </FormGroup>

          <FormGroup>
            <Label>{reqLabel(activeCompany, 'Location')}</Label>
            <Select value={formData.locationId}
              onChange={(e) => {
                const loc = locations.find(l => l.id === e.target.value);
                setFormData({ ...formData, locationId: e.target.value, locationName: loc?.locationName || '' });
              }}>
              <option value="">-- Default --</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.locationName}</option>)}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Employee</Label>
            <Select value={formData.employeeId}
              onChange={(e) => {
                const emp = employees.find(em => em.id === e.target.value);
                setFormData({ ...formData, employeeId: e.target.value, employeeName: emp ? `${emp.firstName} ${emp.lastName || ''}`.trim() : '' });
              }}>
              <option value="">-- None --</option>
              {employees.map(em => <option key={em.id} value={em.id}>{em.firstName} {em.lastName}</option>)}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Job / Project</Label>
            <Select value={formData.jobId}
              onChange={(e) => {
                const j = jobs.find(jj => jj.id === e.target.value);
                setFormData({ ...formData, jobId: e.target.value, jobName: j?.jobName || '' });
              }}>
              <option value="">-- None --</option>
              {jobs.map(j => <option key={j.id} value={j.id}>{j.jobName}</option>)}
            </Select>
          </FormGroup>
        </FormGrid>
      </FormCard>

      {/* ───────── Lines ───────── */}
      <FormCard>
        <LineHeader>
          <SectionTitle style={{ margin: 0 }}>Items (in {formData.foreignCurrency})</SectionTitle>
          <AddLineButton type="button" onClick={addLine}><FiPlus /> Add Line</AddLineButton>
        </LineHeader>

        <LineTableContainer>
          <LineTable>
            <thead>
              <tr>
                <LineTh style={{ width: 36 }}>#</LineTh>
                <LineTh style={{ minWidth: 240 }}>Item</LineTh>
                <LineTh style={{ width: 90, textAlign: 'right' }}>Qty</LineTh>
                <LineTh style={{ width: 80 }}>UoM</LineTh>
                <LineTh style={{ width: 100, textAlign: 'right' }}>Weight (kg)</LineTh>
                <LineTh style={{ width: 100, textAlign: 'right' }}>Volume (m³)</LineTh>
                <LineTh style={{ width: 120, textAlign: 'right' }}>Unit Price ({formData.foreignCurrency})</LineTh>
                <LineTh style={{ width: 120, textAlign: 'right' }}>Foreign Total</LineTh>
                <LineTh style={{ width: 130, textAlign: 'right' }}>{baseCurrency} Total</LineTh>
                <LineTh style={{ width: 40 }}></LineTh>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((line, i) => {
                const enriched = landed.lines[i] || { foreignTotal: 0, baseTotal: 0 };
                return (
                  <React.Fragment key={i}><tr>
                    <LineTd>{i + 1}</LineTd>
                    <LineTd>
                      <ItemSelect
                        ref={(el) => { lineItemRefs.current[i] = el; }}
                        items={items}
                        selectedItemId={line.itemId}
                        onChange={(itemId) => handleItemPicked(i, itemId)}
                      />
                    </LineTd>
                    <LineTd>
                      <LineInput type="number" min="0" step="0.01"
                        value={line.quantity}
                        onChange={(e) => updateLine(i, { quantity: e.target.value })}
                        disabled={line.hasSize}
                        title={line.hasSize ? 'Auto-computed from L × H × Pieces' : undefined}
                        style={{ textAlign: 'right', background: line.hasSize ? '#F1F5F9' : undefined }} />
                    </LineTd>
                    <LineTd>
                      {(() => {
                        const itm = items.find(it => it.id === line.itemId);
                        const itmUnits = itm && Array.isArray(itm.units) ? itm.units : [];
                        if (itmUnits.length > 1) {
                          return (
                            <LineUnitPicker
                              item={itm}
                              line={line}
                              onChange={(uc) => handleUnitChange(i, uc)}
                            />
                          );
                        }
                        return (
                          <LineInput value={line.unitOfMeasure}
                            onChange={(e) => updateLine(i, { unitOfMeasure: e.target.value })} />
                        );
                      })()}
                    </LineTd>
                    <LineTd>
                      <LineInput type="number" min="0" step="0.01"
                        value={line.weight}
                        onChange={(e) => updateLine(i, { weight: e.target.value })}
                        style={{ textAlign: 'right' }} />
                    </LineTd>
                    <LineTd>
                      <LineInput type="number" min="0" step="0.001"
                        value={line.volume}
                        onChange={(e) => updateLine(i, { volume: e.target.value })}
                        style={{ textAlign: 'right' }} />
                    </LineTd>
                    <LineTd>
                      <LineInput type="number" min="0" step="0.01"
                        value={line.unitPriceForeign}
                        onChange={(e) => updateLine(i, { unitPriceForeign: e.target.value })}
                        onKeyDown={(e) => handleLastFieldEnter(e, i)}
                        style={{ textAlign: 'right' }} />
                    </LineTd>
                    <LineTd style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                      {enriched.foreignTotal?.toLocaleString() || 0}
                    </LineTd>
                    <LineTd style={{ textAlign: 'right', fontFamily: 'monospace', color: colors.text.secondary }}>
                      {formatAmount(enriched.baseTotal || 0)}
                    </LineTd>
                    <LineTd style={{ textAlign: 'center' }}>
                      <RemoveLineBtn onClick={() => removeLine(i)} disabled={lineItems.length === 1}>
                        <FiX />
                      </RemoveLineBtn>
                    </LineTd>
                  </tr>
                  {line.hasSize && (<SizeLineRow line={line} index={i} onChange={(idx, field, value) => updateLine(idx, { [field]: value })} leftColSpan={1} />)}
                  </React.Fragment>
                );
              })}
            </tbody>
          </LineTable>
        </LineTableContainer>
      </FormCard>

      {/* ───────── Charges ───────── */}
      <FormCard>
        <LineHeader>
          <SectionTitle style={{ margin: 0 }}>Import Charges (Duties, Taxes, Freight, Clearing)</SectionTitle>
          <div style={{ display: 'flex', gap: spacing.sm }}>
            <Select value={appliedTemplateId}
              onChange={(e) => applyTemplate(e.target.value)}
              style={{ minWidth: 240 }}>
              <option value="">-- Apply Template --</option>
              {chargeTemplates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.templateName} ({t.chargeCount || t.charges?.length || 0} charges)
                  {t.isDefault ? ' ★' : ''}
                </option>
              ))}
            </Select>
            <AddLineButton type="button" onClick={addCharge}><FiPlus /> Add Charge</AddLineButton>
          </div>
        </LineHeader>

        {charges.length === 0 ? (
          <EmptyChargesNote>
            No charges added. Apply a template above, or click <strong>Add Charge</strong> to enter customs duty,
            sales tax, freight, clearing fees, etc.
          </EmptyChargesNote>
        ) : (
          <ChargesTableWrapper>
            <LineTable style={{ minWidth: 1280 }}>
              <thead>
                <tr>
                  <LineTh style={{ width: 36 }}>#</LineTh>
                  <LineTh style={{ minWidth: 160 }}>Charge</LineTh>
                  <LineTh style={{ width: 100 }}>Type</LineTh>
                  <LineTh style={{ width: 100, textAlign: 'right' }}>Value</LineTh>
                  <LineTh style={{ width: 160 }}>Base</LineTh>
                  <LineTh style={{ minWidth: 200 }}>Payable A/c (Cr)</LineTh>
                  <LineTh style={{ width: 80, textAlign: 'center' }}>Capitalize</LineTh>
                  <LineTh style={{ minWidth: 200 }}>Claim A/c (Dr)</LineTh>
                  <LineTh style={{ width: 130 }}>Allocation</LineTh>
                  <LineTh style={{ width: 110, textAlign: 'right' }}>Computed</LineTh>
                  <LineTh style={{ width: 40 }}></LineTh>
                </tr>
              </thead>
              <tbody>
                {charges.map((c, i) => {
                  const enriched = landed.charges[i] || { chargeAmount: 0, error: null };
                  return (
                    <tr key={i} style={enriched.error ? { background: colors.error.main + '10' } : {}}>
                      <LineTd>{i + 1}</LineTd>
                      <LineTd>
                        <LineInput value={c.chargeName}
                          onChange={(e) => updateCharge(i, { chargeName: e.target.value })}
                          placeholder="Customs Duty" />
                      </LineTd>
                      <LineTd>
                        <LineSelect value={c.type}
                          onChange={(e) => updateCharge(i, { type: e.target.value })}>
                          <option value="percentage">%</option>
                          <option value="amount">Amount</option>
                        </LineSelect>
                      </LineTd>
                      <LineTd>
                        <LineInput type="number" step="0.01" min="0"
                          value={c.value}
                          onChange={(e) => updateCharge(i, { value: e.target.value })}
                          style={{ textAlign: 'right' }} />
                      </LineTd>
                      <LineTd>
                        <LineSelect value={c.base}
                          onChange={(e) => updateCharge(i, { base: e.target.value })}
                          disabled={c.type !== 'percentage'}>
                          <option value="cif">CIF Value</option>
                          <option value="cifPlusDuties">CIF + Prior Charges</option>
                          <option value="manual">Manual / N/A</option>
                        </LineSelect>
                      </LineTd>
                      <DropdownCell>
                        <AccountSelect
                          companyId={activeCompany?.id}
                          value={c.accountId}
                          onChange={(account) => updateCharge(i, {
                            accountId: account?.id || '',
                            accountCode: account?.code || '',
                            accountName: account?.name || ''
                          })}
                          label="Payable"
                        />
                      </DropdownCell>
                      <LineTd style={{ textAlign: 'center' }}>
                        <input type="checkbox"
                          checked={c.capitalize}
                          onChange={(e) => updateCharge(i, { capitalize: e.target.checked })} />
                      </LineTd>
                      <DropdownCell>
                        {c.capitalize ? (
                          <DisabledHint>Inventory (auto)</DisabledHint>
                        ) : (
                          <AccountSelect
                            companyId={activeCompany?.id}
                            value={c.claimAccountId}
                            onChange={(account) => updateCharge(i, {
                              claimAccountId: account?.id || '',
                              claimAccountCode: account?.code || '',
                              claimAccountName: account?.name || ''
                            })}
                            label="Claim"
                          />
                        )}
                      </DropdownCell>
                      <LineTd>
                        <LineSelect value={c.allocationMethod}
                          onChange={(e) => updateCharge(i, { allocationMethod: e.target.value })}
                          disabled={!c.capitalize}>
                          <option value="value">By Value</option>
                          <option value="qty">By Qty</option>
                          <option value="weight">By Weight</option>
                          <option value="volume">By Volume</option>
                          <option value="manual">Manual</option>
                        </LineSelect>
                      </LineTd>
                      <LineTd style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                        {formatAmount(enriched.chargeAmount || 0)}
                      </LineTd>
                      <LineTd style={{ textAlign: 'center' }}>
                        <RemoveLineBtn onClick={() => removeCharge(i)}><FiX /></RemoveLineBtn>
                      </LineTd>
                    </tr>
                  );
                })}
              </tbody>
            </LineTable>
          </ChargesTableWrapper>
        )}

        {/* Engine errors */}
        {landed.errors.length > 0 && (
          <ErrorBox>
            <strong>Allocation issues:</strong>
            <ul>
              {landed.errors.map((err, i) => <li key={i}>{err}</li>)}
            </ul>
          </ErrorBox>
        )}
      </FormCard>

      {/* ───────── Landed Cost Summary ───────── */}
      {lineItems.some(l => l.itemId) && (
        <FormCard>
          <SectionTitle>Landed Cost Summary</SectionTitle>
          <LineTableContainer>
            <LineTable>
              <thead>
                <tr>
                  <LineTh style={{ width: 36 }}>#</LineTh>
                  <LineTh>Item</LineTh>
                  <LineTh style={{ textAlign: 'right' }}>Qty</LineTh>
                  <LineTh style={{ textAlign: 'right' }}>Foreign Cost</LineTh>
                  <LineTh style={{ textAlign: 'right' }}>{baseCurrency} Cost</LineTh>
                  <LineTh style={{ textAlign: 'right' }}>+ Capitalized Charges</LineTh>
                  <LineTh style={{ textAlign: 'right' }}>= Landed Total</LineTh>
                  <LineTh style={{ textAlign: 'right' }}>Landed Unit Cost</LineTh>
                </tr>
              </thead>
              <tbody>
                {landed.lines.map((l, i) => (
                  <tr key={i}>
                    <LineTd>{i + 1}</LineTd>
                    <LineTd>{l.itemName || `Line ${i + 1}`}</LineTd>
                    <LineTd style={{ textAlign: 'right' }}>{l.quantity}</LineTd>
                    <LineTd style={{ textAlign: 'right', fontFamily: 'monospace' }}>{(l.foreignTotal || 0).toLocaleString()}</LineTd>
                    <LineTd style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatAmount(l.baseTotal || 0)}</LineTd>
                    <LineTd style={{ textAlign: 'right', fontFamily: 'monospace', color: colors.primary.main }}>
                      +{formatAmount(l.capitalizedTotal || 0)}
                    </LineTd>
                    <LineTd style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                      {formatAmount(l.landedTotal || 0)}
                    </LineTd>
                    <LineTd style={{ textAlign: 'right', fontFamily: 'monospace', color: colors.success.dark, fontWeight: 700 }}>
                      {formatAmount(l.landedUnitCost || 0)}
                    </LineTd>
                  </tr>
                ))}
              </tbody>
            </LineTable>
          </LineTableContainer>
        </FormCard>
      )}

      {/* ───────── Totals ───────── */}
      <TotalsSection>
        <TotalsTable>
          <tbody>
            <TotalRow>
              <td>Foreign Subtotal ({formData.foreignCurrency})</td>
              <td style={{ fontFamily: 'monospace' }}>{(landed.summary.foreignSubtotal || 0).toLocaleString()}</td>
            </TotalRow>
            <TotalRow>
              <td>× Exchange Rate</td>
              <td style={{ fontFamily: 'monospace' }}>{landed.summary.exchangeRate}</td>
            </TotalRow>
            <TotalRow>
              <td><strong>= {baseCurrency} Subtotal (Vendor Payable)</strong></td>
              <td style={{ fontFamily: 'monospace' }}><strong>{formatAmount(landed.summary.baseSubtotal || 0)}</strong></td>
            </TotalRow>
            <TotalRow>
              <td>+ Capitalized Charges</td>
              <td style={{ fontFamily: 'monospace', color: colors.primary.main }}>+{formatAmount(landed.summary.capitalizedTotal || 0)}</td>
            </TotalRow>
            <TotalRow>
              <td><strong>= Inventory Value (Dr Inventory Control)</strong></td>
              <td style={{ fontFamily: 'monospace' }}><strong>{formatAmount(landed.summary.inventoryValue || 0)}</strong></td>
            </TotalRow>
            <TotalRow>
              <td>+ Non-Capitalized Charges (claimable inputs)</td>
              <td style={{ fontFamily: 'monospace', color: colors.info.dark }}>+{formatAmount(landed.summary.nonCapitalizedTotal || 0)}</td>
            </TotalRow>
            <TotalRow style={{ fontSize: typography.fontSize.lg, background: colors.primary.main + '10' }}>
              <td><strong>Grand Total Owed (all credits)</strong></td>
              <td style={{ fontFamily: 'monospace' }}><strong>{formatAmount(landed.summary.grandTotalOwed || 0)}</strong></td>
            </TotalRow>
          </tbody>
        </TotalsTable>
      </TotalsSection>

      <FormCard>
        <FormGrid>
          <FormGroup style={{ gridColumn: 'span 2' }}>
            <Label>Notes (visible on print)</Label>
            <TextArea rows={2} value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
          </FormGroup>
          <FormGroup style={{ gridColumn: 'span 2' }}>
            <Label>Internal Notes</Label>
            <TextArea rows={2} value={formData.internalNotes}
              onChange={(e) => setFormData({ ...formData, internalNotes: e.target.value })} />
          </FormGroup>
        </FormGrid>
      </FormCard>
    </PageLayout>
  );
};

// ─── Local Styles ───
const LineHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: ${spacing.sm}; gap: ${spacing.md}; flex-wrap: wrap;
`;

const CurrencyTag = styled.span`
  display: inline-block; padding: 2px 8px;
  background: ${colors.info.main}20; color: ${colors.info.dark};
  border-radius: ${borderRadius.sm}; font-size: 11px; font-weight: 600;
  margin-right: 6px;
`;

const SmallRate = styled.span`
  font-size: 11px; color: ${colors.text.secondary}; font-family: monospace;
`;

const DisabledHint = styled.div`
  padding: 6px ${spacing.sm};
  font-size: 11px; font-style: italic;
  color: ${colors.text.tertiary};
  background: ${colors.neutral[100]};
  border: 1px dashed ${colors.border.light};
  border-radius: ${borderRadius.sm};
  text-align: center;
`;

// Charges table wrapper — overflow MUST be visible so AccountSelect dropdowns
// (Payable Account / Claim Account) can extend below the row without being clipped.
const ChargesTableWrapper = styled.div`
  overflow: visible;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
`;

// Cell override — shared LineTd has `overflow: hidden` which clips AccountSelect's
// dropdown menu. Use this for cells that contain AccountSelect.
const DropdownCell = styled.td`
  padding: 4px 6px;
  font-size: ${typography.fontSize.sm};
  border-bottom: 1px solid ${colors.neutral[100]};
  overflow: visible;
  position: relative;
  z-index: 1;
`;

const EmptyChargesNote = styled.div`
  padding: ${spacing.lg};
  text-align: center;
  color: ${colors.text.secondary};
  background: ${colors.neutral[50]};
  border: 1px dashed ${colors.border.light};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
`;

const ErrorBox = styled.div`
  margin-top: ${spacing.md};
  padding: ${spacing.md};
  background: ${colors.error.main}10;
  border-left: 3px solid ${colors.error.main};
  border-radius: ${borderRadius.sm};
  color: ${colors.error.dark};
  font-size: ${typography.fontSize.sm};

  ul { margin: 6px 0 0 ${spacing.lg}; padding: 0; }
  li { margin: 2px 0; }
`;

export default ImportPurchaseInvoice;
